import { type ReactNode } from 'react'
import { isNotEmpty, type CardMessageTemplate } from '@terros/common'
import { Box, Group, Input, Stack, Text, Textarea } from '@mantine/core'
import { useTranslation } from '@front/model'
import { TButton, TCard } from '@components'
import { type CompanyCardFormProps } from './CompanyCardFields'

export const DEFAULT_MESSAGE_BODY =
  "Hi {first_name}, it was great meeting you today! Here's my digital card with everything about me. Reach out anytime."

const VARIABLES = ['{first_name}', '{last_name}', '{rep_name}']

export const MessageTemplateFields = ({ form }: CompanyCardFormProps): ReactNode => {
  const { t } = useTranslation()
  const templates = form.values.messageTemplates ?? []
  const body = templates[0]?.body ?? DEFAULT_MESSAGE_BODY

  function updateBody(updatedBody: string): void {
    const updatedTemplate: CardMessageTemplate = {
      name: templates[0]?.name ?? 'Default',
      tag: templates[0]?.tag ?? 'default',
      body: updatedBody,
    }
    form.setFieldValue('messageTemplates', [updatedTemplate, ...templates.slice(1)])
  }

  function appendVariable(variable: string): void {
    updateBody(`${body}${isNotEmpty(body) ? ' ' : ''}${variable}`)
  }

  return (
    <TCard>
      <Stack>
        <TCard.Header>
          <TCard.Title>{t('Message template')}</TCard.Title>
        </TCard.Header>
        <Box pos='relative'>
          <Textarea
            autosize
            description={t('Your card link is always added to the end of the message.')}
            minRows={4}
            styles={{ input: { paddingBottom: 'calc(var(--mantine-spacing-lg) + var(--mantine-spacing-xs))' } }}
            value={body}
            onChange={(event) => updateBody(event.currentTarget.value)}
          />
          <Text
            c='dimmed'
            pos='absolute'
            size='xs'
            style={{ bottom: 'var(--mantine-spacing-xs)', left: 'var(--mantine-spacing-sm)', pointerEvents: 'none' }}
          >
            {t('{{count}} characters', { count: body.length })}
          </Text>
        </Box>
        <Input.Wrapper label={t('Available variables')} labelElement='div'>
          <Group gap='xs' mt='xs'>
            {VARIABLES.map((variable) => (
              <TButton key={variable} size='xs' variant='secondary' onClick={() => appendVariable(variable)}>
                {variable}
              </TButton>
            ))}
          </Group>
        </Input.Wrapper>
      </Stack>
    </TCard>
  )
}
